/**
 * Appointment Slots utilities.
 *
 * Generate and manage bookable time slots for services
 * (haircut, consultation, cleaning, etc.)
 *
 * Slot config in listing.publicData.slotConfig = {
 *   duration: 30,        — minutes per appointment
 *   bufferMinutes: 10,   — gap between appointments
 *   startHour: 9,        — business hours start (0-23)
 *   endHour: 17,         — business hours end (0-23)
 *   daysOfWeek: [1,2,3,4,5], — 0=Sun, 6=Sat
 * }
 *
 * Booked slots stored in listing.metadata.bookedSlots[] = [ISO datetime strings]
 */

export const DEFAULT_SLOT_DURATION = 60; // minutes
export const DEFAULT_BUFFER = 0;

/**
 * Generate available time slots for a given date.
 * @param {Date} date
 * @param {Object} config — { duration, bufferMinutes, startHour, endHour, daysOfWeek }
 * @param {string[]} bookedSlots — ISO datetime strings that are already booked
 * @returns {Date[]}
 */
export const generateSlots = (date, config = {}, bookedSlots = []) => {
  const {
    duration = DEFAULT_SLOT_DURATION,
    bufferMinutes = DEFAULT_BUFFER,
    startHour = 9,
    endHour = 17,
    daysOfWeek = [1, 2, 3, 4, 5],
  } = config;

  if (!daysOfWeek.includes(date.getDay())) return [];

  const slots = [];
  const slotInterval = duration + bufferMinutes;
  const startMinutes = startHour * 60;
  const endMinutes = endHour * 60;

  const bookedSet = new Set(bookedSlots.map(s => new Date(s).getTime()));

  for (let min = startMinutes; min + duration <= endMinutes; min += slotInterval) {
    const slotDate = new Date(date);
    slotDate.setUTCHours(0, 0, 0, 0);
    slotDate.setMinutes(slotDate.getMinutes() + min);
    if (!bookedSet.has(slotDate.getTime())) {
      slots.push(slotDate);
    }
  }
  return slots;
};

/**
 * Check if a slot is available.
 * @param {Date|string} slot
 * @param {string[]} bookedSlots
 * @returns {boolean}
 */
export const isSlotAvailable = (slot, bookedSlots = []) => {
  const slotMs = new Date(slot).getTime();
  return !bookedSlots.some(s => new Date(s).getTime() === slotMs);
};

/**
 * Format slot time for display (HH:MM).
 * @param {Date} slot
 * @returns {string}
 */
export const formatSlotTime = (slot) => {
  const d = new Date(slot);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Validate slot config.
 * @param {Object} config
 * @returns {string|null}
 */
export const validateSlotConfig = (config) => {
  if (!config) return 'Config required';
  if (!config.duration || config.duration < 5 || config.duration > 480) return 'Duration must be 5-480 minutes';
  if (config.startHour >= config.endHour) return 'End hour must be after start hour';
  if (!Array.isArray(config.daysOfWeek) || config.daysOfWeek.length === 0) return 'At least one working day required';
  return null;
};
